import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section
      className={cn(
        'rounded-card border border-line bg-surface shadow-card',
        padded && 'p-5',
        className
      )}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-4 flex items-center justify-between gap-4', className)}>
      <div className="min-w-0">
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
        {description && <p className="mt-0.5 text-[13px] text-muted">{description}</p>}
      </div>
      {action}
    </header>
  );
}

/** Page-level heading with optional actions on the right. */
export function PageHeader({
  title,
  eyebrow,
  description,
  actions,
}: {
  title: string;
  /** Small label above the title, e.g. the active group's name. */
  eyebrow?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && (
          <p className="mb-1 text-[12px] font-semibold uppercase tracking-[0.08em] text-primary">
            {eyebrow}
          </p>
        )}
        <h1 className="text-[26px] font-bold leading-tight tracking-tight sm:text-[28px]">
          {title}
        </h1>
        {description && <p className="mt-1.5 text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

const STAT_TONES = {
  default: { value: 'text-fg', chip: 'bg-primary/10 text-primary' },
  primary: { value: 'text-primary', chip: 'bg-primary/10 text-primary' },
  success: { value: 'text-success', chip: 'bg-success/12 text-success' },
  danger: { value: 'text-danger', chip: 'bg-danger/12 text-danger' },
} as const;

export function StatTile({
  label,
  value,
  caption,
  icon: Icon,
  tone = 'default',
  className,
}: {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  icon?: LucideIcon;
  tone?: keyof typeof STAT_TONES;
  className?: string;
}) {
  const styles = STAT_TONES[tone];

  return (
    <div
      className={cn('min-w-0 rounded-card border border-line bg-surface p-4 shadow-card sm:p-5', className)}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="truncate text-[13px] font-medium text-muted">{label}</p>
        {Icon && (
          <span
            className={cn(
              'inline-flex size-8 shrink-0 items-center justify-center rounded-xl sm:size-9',
              styles.chip
            )}
          >
            <Icon size={17} strokeWidth={2.2} />
          </span>
        )}
      </div>
      <p
        className={cn(
          'tnum mt-2 truncate text-[22px] font-bold tracking-tight sm:text-[26px]',
          styles.value
        )}
      >
        {value}
      </p>
      {caption && <p className="mt-0.5 truncate text-[12px] text-muted">{caption}</p>}
    </div>
  );
}
